import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, Injector, afterNextRender, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { TooltipModule } from 'primeng/tooltip';

import { NAVIGATION_DOMAINS } from '../../../config/navigation-domains.config';
import {
  NavigationDestinationInterface,
  NavigationDomainInterface,
  NavigationSectionInterface
} from '../../../interfaces';
import type { PermissionActionType } from '../../../../modules/auth/interfaces';
import { AuthFacade } from '../../../../modules/auth/facades';
import { globalTourAnchor, panelItemTourAnchor } from '../../../onboarding/registry/guided-tour.registry';
import { NavigationCapabilitiesService, NavigationStateService, PermissionsService, navigationKey } from '../../../services';

/** Acima desta quantidade de destinos o painel exibe a busca interna. */
const SEARCH_THRESHOLD = 6;

function normalizeText(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

@Component({
  selector: 'app-side-bar',
  standalone: true,
  imports: [NgTemplateOutlet, RouterModule, LucideAngularModule, TooltipModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  host: {
    '(document:keydown.escape)': 'handleEscape($event)'
  }
})
export class SidebarComponent {
  protected readonly nav = inject(NavigationStateService);

  private readonly router = inject(Router);
  private readonly capabilities = inject(NavigationCapabilitiesService);
  private readonly injector = inject(Injector);
  private readonly authFacade = inject(AuthFacade);
  private readonly permissionsService = inject(PermissionsService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');
  private readonly scrollByDomain = new Map<string, number>();

  private readonly visibleModules = typeof this.permissionsService?.visibleModules === 'function'
    ? this.permissionsService.visibleModules
    : toSignal(this.permissionsService.visibleModules$, { initialValue: [] });
  private readonly company = typeof this.authFacade?.company === 'function'
    ? this.authFacade.company
    : toSignal(this.authFacade.company$, { initialValue: null });

  protected readonly tourAnchor = globalTourAnchor;
  protected readonly panelAnchor = panelItemTourAnchor;
  protected readonly searchQuery = signal('');
  protected readonly companyName = computed(
    () => this.company()?.tradeName ?? this.company()?.legalName ?? 'Loja ativa'
  );

  protected readonly companyInitial = computed(() => this.companyName().trim().charAt(0).toUpperCase() || 'L');

  protected readonly domains = computed<NavigationDomainInterface[]>(() => {
    // lê a lista de módulos para o computed reagir a mudanças de acesso
    this.visibleModules();
    const features = this.capabilities.features();
    const isAllowed = (moduleKey: string, action: PermissionActionType = 'view'): boolean =>
      this.permissionsService.can(moduleKey, action);
    const isEnabled = (item: NavigationDestinationInterface): boolean =>
      isAllowed(item.moduleKey, item.action) &&
      (!item.feature || features.has(item.feature)) &&
      (!item.requiredPermission || this.permissionsService.hasPermission(item.requiredPermission));

    return NAVIGATION_DOMAINS.reduce<NavigationDomainInterface[]>((visible, domain) => {
      if (domain.requiredPermission && !this.permissionsService.hasPermission(domain.requiredPermission)) {
        return visible;
      }

      if (domain.directRoute) {
        return domain.moduleKey && isAllowed(domain.moduleKey) ? [...visible, domain] : visible;
      }

      const sections = domain.sections
        .map((section) => ({ ...section, items: section.items.filter(isEnabled) }))
        .filter((section) => section.items.length > 0);

      return sections.length > 0 ? [...visible, { ...domain, sections }] : visible;
    }, []);
  });

  private readonly hasInventoryModule = computed(() =>
    this.visibleModules().some((modulePermission) => modulePermission.key === 'inventory')
  );

  protected readonly mainDomains = computed(() =>
    this.domains().filter((domain) => domain.placement !== 'footer')
  );
  protected readonly footerDomains = computed(() =>
    this.domains().filter((domain) => domain.placement === 'footer')
  );

  protected readonly panelDomain = computed<NavigationDomainInterface | null>(() => {
    const panelDomains = this.domains().filter((domain) => !domain.directRoute);

    return panelDomains.find((domain) => domain.id === this.nav.panelDomainId()) ?? panelDomains[0] ?? null;
  });

  protected readonly showSearch = computed(() => {
    const total = this.panelDomain()?.sections.reduce((sum, section) => sum + section.items.length, 0) ?? 0;
    return total > SEARCH_THRESHOLD;
  });

  protected readonly visibleSections = computed<NavigationSectionInterface[]>(() => {
    const sections = this.panelDomain()?.sections ?? [];
    const query = normalizeText(this.searchQuery());

    if (!query) {
      return sections;
    }

    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => normalizeText(item.label).includes(query))
      }))
      .filter((section) => section.items.length > 0);
  });

  protected readonly homeRoute = computed(() => {
    const [firstDomain] = this.domains();
    return firstDomain?.directRoute ?? firstDomain?.sections[0]?.items[0]?.route ?? '/acesso-negado';
  });

  constructor() {
    // as regras do estoque dependem do onboarding: carrega ao entrar e relê ao abrir o painel do catálogo
    effect(() => {
      if (this.hasInventoryModule()) {
        untracked(() => this.capabilities.refreshInventory());
      }
    });
    effect(() => {
      if (this.hasInventoryModule() && this.nav.isOpen() && this.nav.panelDomainId() === 'catalog') {
        untracked(() => this.capabilities.refreshInventory());
      }
    });

    effect(
      () => {
        const domainId = this.panelDomain()?.id;
        this.searchQuery.set('');
        this.restoreScroll(domainId);
      },
      { allowSignalWrites: true }
    );
  }

  protected selectDomain(domain: NavigationDomainInterface): void {
    this.nav.toggleDomain(domain.id);
  }

  protected handleDestinationClick(event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    this.nav.destinationSelected();
  }

  protected openFirstResult(): void {
    const [firstSection] = this.visibleSections();
    const firstItem = firstSection?.items[0];

    if (!firstItem) {
      return;
    }

    void this.router.navigate([firstItem.route], { queryParams: firstItem.queryParams });
    this.nav.destinationSelected();
  }

  protected rememberScroll(event: Event): void {
    const domainId = this.panelDomain()?.id;

    if (domainId) {
      this.scrollByDomain.set(domainId, (event.target as HTMLElement).scrollTop);
    }
  }

  protected handleEscape(event: Event): void {
    if (event.defaultPrevented) {
      return;
    }

    const focusWasInPanel = this.host.nativeElement
      .querySelector('[data-testid="sidebar-panel"]')
      ?.contains(document.activeElement) ?? false;
    const domainId = this.panelDomain()?.id;

    if (this.nav.escape() && focusWasInPanel && domainId) {
      this.host.nativeElement
        .querySelector<HTMLElement>(`[data-domain-id="${domainId}"]`)
        ?.focus();
    }
  }

  protected isDomainActive(domain: NavigationDomainInterface): boolean {
    return this.nav.activeDomainId() === domain.id;
  }

  /** Fundo tingido: o domínio que o painel mostra, ou a página atual enquanto o painel está fechado. */
  protected isDomainSelected(domain: NavigationDomainInterface): boolean {
    return this.isDomainExpanded(domain) || (!this.nav.isOpen() && this.isDomainActive(domain));
  }

  protected isDomainExpanded(domain: NavigationDomainInterface): boolean {
    return this.nav.isOpen() && this.panelDomain()?.id === domain.id;
  }

  protected isDestinationActive(destination: NavigationDestinationInterface): boolean {
    return this.nav.activeRoute() === navigationKey(destination);
  }

  protected testId(destination: NavigationDestinationInterface): string {
    return navigationKey(destination).replace(/^\//, '').replace(/[/?=&]/g, '-');
  }

  private restoreScroll(domainId: string | undefined): void {
    afterNextRender(
      () => {
        const element = this.scroller()?.nativeElement;

        if (element) {
          element.scrollTop = (domainId && this.scrollByDomain.get(domainId)) || 0;
        }
      },
      { injector: this.injector }
    );
  }
}
